import type {
  CustomerSaleResponse,
  SaleItem,
} from '@/api/customer/customerSaleQuery/types';
import { getMembershipDiscount } from '@/utils/membershipUtils';

import type {
  AppointmentCosts,
  AppointmentState,
  CalculateCanRebookParams,
  CalculateCanRescheduleParams,
  CalculateTippingEligibilityParams,
  ProcessedAppointmentData,
} from './types';

/**
 * Extract all appointment items from sale data
 */
export const extractAppointmentItems = (
  saleData: CustomerSaleResponse,
): SaleItem[] => {
  return saleData.items.filter((item) => item.type === 'appointment');
};

/**
 * Calculate total subtotal across all appointment items
 */
export const calculateSubtotal = (appointmentItems: SaleItem[]): number => {
  return appointmentItems.reduce(
    (sum, item) =>
      sum + (item.appointment.costs?.services?.totalWithoutTaxes ?? 0),
    0,
  );
};

/**
 * Calculate total tax across all appointment items
 */
export const calculateTax = (appointmentItems: SaleItem[]): number => {
  return appointmentItems.reduce(
    (sum, item) => sum + (item.appointment.costs?.taxes?.total ?? 0),
    0,
  );
};

/**
 * Calculate total booking fees across all appointment items
 */
export const calculateFees = (appointmentItems: SaleItem[]): number => {
  return appointmentItems.reduce(
    (sum, item) => sum + (item.appointment.costs?.fees?.total ?? 0),
    0,
  );
};

/**
 * Calculate total tip across all appointment items
 */
export const calculateTotalTip = (appointmentItems: SaleItem[]): number => {
  return appointmentItems.reduce((sum, item) => sum + (item.tipAmount || 0), 0);
};

/**
 * Calculate total cost from components
 */
export const calculateTotalCost = (costs: AppointmentCosts): number => {
  return (
    costs.subtotal +
    costs.tax +
    costs.fees +
    costs.tip -
    costs.promo -
    costs.membership -
    costs.giftCard
  );
};

/**
 * Calculate promo amount from sale data
 */
export const calculatePromoAmount = (
  saleData: CustomerSaleResponse,
): number => {
  // Check breakdown first (most reliable)
  if (saleData.breakdown?.promoAmount) {
    return saleData.breakdown.promoAmount;
  }

  // Fallback to adjustments array
  if (saleData.adjustments) {
    const promoAdjustment = saleData.adjustments.find(
      (adj) => adj.type === 'promo',
    );
    if (promoAdjustment) {
      // Amount is negative in adjustments, return as positive
      return Math.abs(promoAdjustment.amount);
    }
  }

  return 0;
};

/**
 * Get promo code name from sale data
 */
export const getPromoCodeName = (
  saleData: CustomerSaleResponse,
): string | null => {
  if (saleData.adjustments) {
    const promoAdjustment = saleData.adjustments.find(
      (adj) => adj.type === 'promo',
    );
    return promoAdjustment?.name || null;
  }
  return null;
};

/**
 * Gift-card amount applied to the sale (positive cents; 0 if none). A gift card
 * is a payment rather than an adjustment, so it is read off `payments`.
 */
export const calculateGiftCardAmount = (
  saleData: CustomerSaleResponse,
): number =>
  saleData.payments?.find((payment) => payment.type === 'gift_card')?.amount ??
  0;

/**
 * Calculate membership discount from sale data (positive cents; 0 if none).
 */
export const calculateMembershipAmount = (
  saleData: CustomerSaleResponse,
): number => getMembershipDiscount(saleData.adjustments);

/**
 * Calculate all appointment costs
 */
export const calculateAppointmentCosts = (
  appointmentItems: SaleItem[],
  saleData: CustomerSaleResponse,
): AppointmentCosts => {
  const subtotal = calculateSubtotal(appointmentItems);
  const tax = calculateTax(appointmentItems);
  const fees = calculateFees(appointmentItems);
  const tip = calculateTotalTip(appointmentItems);
  const promo = calculatePromoAmount(saleData);
  const membership = calculateMembershipAmount(saleData);
  const giftCard = calculateGiftCardAmount(saleData);
  const total = subtotal + tax + fees + tip - promo - membership - giftCard;

  return {
    subtotal,
    tax,
    fees,
    tip,
    promo,
    membership,
    giftCard,
    total,
  };
};

/**
 * Check if appointment is cancelled
 */
export const isCancelled = (saleData: CustomerSaleResponse): boolean => {
  return saleData.bookingInfo?.canceled || false;
};

/**
 * Check if all appointments are in the past or completed
 */
export const isPastAppointment = (appointmentItems: SaleItem[]): boolean => {
  return appointmentItems.every(
    (item) =>
      new Date(item.appointment.endTime) < new Date() ||
      item.appointment.status === 2,
  );
};

/**
 * Calculate appointment state (cancelled, past)
 */
export const calculateAppointmentState = (
  saleData: CustomerSaleResponse,
  appointmentItems: SaleItem[],
): AppointmentState => {
  return {
    isCancelled: isCancelled(saleData),
    isPastAppointment: isPastAppointment(appointmentItems),
  };
};

/**
 * Whether a single item's barber allows tips at the relevant stage.
 *
 * Tipping is a per-barber setting, not a shop setting — this mirrors
 * water-on-web, which gates exclusively on the barber (`barber.earlyTipping`
 * for upcoming appointments, `barber.lateTipping` for past ones) and never
 * consults a shop-level toggle.
 *
 * Evaluated per item rather than across the sale: a group appointment can span
 * barbers with different settings, and one barber with tipping disabled must not
 * suppress tipping for the others. water-on-web does the same — its
 * `AppointmentBreakdown` renders per sale-order item off that item's own barber.
 *
 * Note: the API defines `earlyTipping` as a virtual alias of `tipEnabled` on
 * both shop and barber, so the early case is equivalent to checking
 * `tipEnabled`. `lateTipping` is an independent column and must be read
 * directly.
 */
export const barberAllowsTips = (
  item: Pick<SaleItem, 'barber'>,
  isPast: boolean,
): boolean => {
  return isPast
    ? item.barber?.lateTipping === true
    : item.barber?.earlyTipping === true;
};

/**
 * Sale-level tipping gate: whether tips can be added or changed on this sale at
 * all, independent of any individual barber.
 *
 * The per-barber decision is made per item by `barberAllowsTips`, so a group
 * appointment spanning barbers with different settings resolves correctly.
 */
export const calculateTippingEligibility = (
  params: CalculateTippingEligibilityParams,
): boolean => {
  // This flag determines if we can add OR change tips on this sale
  if (!params.canApplyLateTipping) {
    return false;
  }

  // Cannot add/change tip if cancelled
  return !params.isCancelled;
};

/**
 * Calculate if appointment can be cancelled based on API data
 * Uses shop.canCustomerCancel, barber.canCustomerCancel, and bookingInfo.canCancelBefore from API
 */
export const calculateCanCancel = (
  saleData: CustomerSaleResponse,
  appointmentItems: SaleItem[],
  isPast: boolean,
  cancelled: boolean,
): boolean => {
  // Cannot cancel if already cancelled
  if (cancelled) {
    return false;
  }

  // Cannot cancel if past appointment
  if (isPast) {
    return false;
  }

  // Check shop-level cancellation permission
  // If explicitly set to false, disallow cancellation
  if (saleData.shop.canCustomerCancel === false) {
    return false;
  }

  // Check if any barber disallows cancellation
  // If any barber explicitly sets canCustomerCancel to false, disallow cancellation
  const anyBarberDisallowsCancel = appointmentItems.some(
    (item) => item.barber?.canCustomerCancel === false,
  );
  if (anyBarberDisallowsCancel) {
    return false;
  }

  // Check if we're past the cancellation deadline (canCancelBefore)
  if (saleData.bookingInfo?.canCancelBefore) {
    const cancelDeadline = new Date(saleData.bookingInfo.canCancelBefore);
    const now = new Date();
    if (now >= cancelDeadline) {
      return false;
    }
  }

  return true;
};

/**
 * Calculate if appointment can be rescheduled
 */
export const calculateCanReschedule = (
  params: CalculateCanRescheduleParams,
): boolean => {
  // Base condition: can't reschedule if past or cancelled
  if (params.isPastAppointment || params.isCancelled) {
    return false;
  }

  // Can't reschedule group appointments (2+ people)
  // Group appointments must be rebooked instead
  if (params.isGroupAppointment) {
    return false;
  }

  // Check if we're past the reschedule deadline
  if (params.canCancelBefore) {
    const rescheduleDeadline = new Date(params.canCancelBefore);
    const now = new Date();
    const isPastDeadline = now >= rescheduleDeadline;

    // If past deadline, disable reschedule
    if (isPastDeadline) {
      return false;
    }
  }

  return true;
};

/**
 * Calculate if appointment can be rebooked
 * Mirrors backend validation: shop must be enabled, barber must be enabled
 */
export const calculateCanRebook = (
  params: CalculateCanRebookParams,
): boolean => {
  // Can only rebook past or cancelled appointments
  if (!params.isPastAppointment && !params.isCancelled) {
    return false;
  }

  // Shop must be enabled (backend checks shop.enabled)
  if (params.shopEnabled === false) {
    return false;
  }

  return true;
};

/**
 * Process all appointment data from sale data
 * This is the main entry point that orchestrates all data processing
 */
export const processAppointmentData = (
  saleData: CustomerSaleResponse,
): ProcessedAppointmentData => {
  // Extract appointment items
  const appointmentItems = extractAppointmentItems(saleData);

  // Calculate costs
  const costs = calculateAppointmentCosts(appointmentItems, saleData);

  // Calculate state
  const state = calculateAppointmentState(saleData, appointmentItems);

  // Calculate tipping eligibility
  const canAddTip = calculateTippingEligibility({
    isCancelled: state.isCancelled,
    totalTip: costs.tip,
    canApplyLateTipping: saleData.canApplyLateTipping,
    isPastAppointment: state.isPastAppointment,
  });

  // Calculate action permissions
  const canCancel = calculateCanCancel(
    saleData,
    appointmentItems,
    state.isPastAppointment,
    state.isCancelled,
  );

  const canReschedule = calculateCanReschedule({
    isPastAppointment: state.isPastAppointment,
    isCancelled: state.isCancelled,
    canCancelBefore: saleData.bookingInfo?.canCancelBefore,
    isGroupAppointment: appointmentItems.length > 1,
  });

  const canRebook = calculateCanRebook({
    isPastAppointment: state.isPastAppointment,
    isCancelled: state.isCancelled,
    shopEnabled: saleData.shop.enabled,
  });

  return {
    appointmentItems,
    costs,
    state,
    actions: {
      canCancel,
      canReschedule,
      canRebook,
      canAddTip,
    },
  };
};
